<script setup lang="ts">
import { computed } from 'vue';
import { HIT_VERDICT_LABELS, type HitVerdict } from './caseDetail';
import CopyButton from './CopyButton.vue';

const props = defineProps<{
  reasoning: string;
  tone: HitVerdict;
  reviewerVerdict?: HitVerdict;
  evidence?: string[];
}>();

const toneChipStyles: Record<HitVerdict, string> = {
  true_positive: 'bg-red-50 text-red-700',
  false_positive: 'bg-green-50 text-green-700',
  unknown: 'bg-amber-50 text-amber-700',
};

const agreement = computed(() => {
  if (!props.reviewerVerdict) return '';
  return props.reviewerVerdict === props.tone
    ? 'Your verdict agrees with the agent'
    : 'Your verdict differs from the agent — the report will note the override';
});

// Each evidence entry is "<url> — <note>"; falls back to plain text if an
// entry doesn't start with a url.
const URL_PREFIX = /^(https?:\/\/\S+)\s*(?:—\s*)?(.*)$/;
const evidenceItems = computed(
  () =>
    props.evidence?.map((entry) => {
      const match = entry.match(URL_PREFIX);
      return match ? { url: match[1], note: match[2] } : { url: undefined, note: entry };
    }) ?? []
);
</script>

<template>
  <div class="border-t border-gray-100 bg-gray-50 p-4" data-test="agent-assessment-box">
    <div class="flex items-center justify-between gap-2">
      <span class="flex items-center gap-1">
        <span
          class="inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide"
          :class="toneChipStyles[tone]"
        >
          {{ HIT_VERDICT_LABELS[tone] }}
        </span>
        <CopyButton :value="HIT_VERDICT_LABELS[tone]" label="status" />
      </span>
      <CopyButton :value="reasoning" label="bot review" />
    </div>
    <p class="mt-2.5 text-[13px] break-words whitespace-pre-wrap text-gray-700">
      {{ reasoning }}
    </p>
    <ul
      v-if="evidenceItems.length"
      class="mt-2.5 flex flex-col gap-1.5"
      data-test="agent-evidence-list"
    >
      <li
        v-for="(item, index) in evidenceItems"
        :key="index"
        class="text-[12px] break-words text-gray-500"
      >
        <span v-if="item.url" class="inline-flex items-baseline gap-1">
          <a
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
          >
            {{ item.url }}
          </a>
          <CopyButton :value="item.url ?? ''" label="source URL" />
        </span>
        <span v-if="item.note">{{ item.url ? ' — ' : '' }}{{ item.note }}</span>
      </li>
    </ul>
    <p v-if="agreement" class="mt-2 text-[11.5px] text-gray-400">{{ agreement }}</p>
  </div>
</template>
